import { useState, useRef, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Bot,
  X,
  Send,
  User,
  Loader2,
  Maximize2,
  Minimize2,
  TrendingUp,
  Users,
  CreditCard,
  AlertTriangle,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { getAppConfigService } from '@/domain/app-config'

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
  suggestions?: string[]
}

const quickActions = [
  { icon: <TrendingUp className="h-4 w-4" />, label: 'สรุปยอดวันนี้', query: 'สรุปยอดฝาก-ถอนวันนี้' },
  { icon: <Users className="h-4 w-4" />, label: 'สมาชิกใหม่', query: 'มีสมาชิกใหม่กี่คนวันนี้' },
  { icon: <CreditCard className="h-4 w-4" />, label: 'รอดำเนินการ', query: 'มีรายการรอดำเนินการกี่รายการ' },
  { icon: <AlertTriangle className="h-4 w-4" />, label: 'แจ้งเตือน', query: 'มีการแจ้งเตือนอะไรบ้าง' },
]

const mockResponses: Record<string, { content: string; suggestions?: string[] }> = {
  'สรุปยอดฝาก-ถอนวันนี้': {
    content: `📊 **สรุปยอดวันนี้ (27 พ.ย. 2025)**

💰 **ยอดฝาก:** ฿15,250,000 (+12.5% จากเมื่อวาน)
💸 **ยอดถอน:** ฿12,850,000 (+8.3% จากเมื่อวาน)
📈 **กำไรสุทธิ:** ฿2,400,000

🔢 **จำนวนธุรกรรม:**
- ฝาก: 1,520 รายการ
- ถอน: 938 รายการ

✅ อัตราสำเร็จ: 98.5%`,
    suggestions: ['ดูรายละเอียดเพิ่มเติม', 'เปรียบเทียบกับสัปดาห์ที่แล้ว', 'แสดงกราฟ'],
  },
  'มีสมาชิกใหม่กี่คนวันนี้': {
    content: `👥 **สมาชิกใหม่วันนี้**

📝 **สมัครใหม่:** 156 คน (+25% จากเมื่อวาน)

📊 **ช่วงเวลาที่สมัครมากที่สุด:**
1. 20:00-22:00 - 42 คน
2. 18:00-20:00 - 35 คน
3. 16:00-18:00 - 28 คน

💳 **สมาชิกที่ฝากเงินแล้ว:** 89 คน (57%)
💰 **ยอดฝากจากสมาชิกใหม่:** ฿1,250,000`,
    suggestions: ['ดูรายชื่อสมาชิกใหม่', 'วิเคราะห์แหล่งที่มา', 'ส่งโปรโมชั่นต้อนรับ'],
  },
  'มีรายการรอดำเนินการกี่รายการ': {
    content: `⏳ **รายการรอดำเนินการ**

🔴 **ถอนรอดำเนินการ:** 24 รายการ
   - ยอดรวม: ฿1,850,000
   - รอนานที่สุด: 15 นาที

🟡 **ฝากรอตรวจสอบ:** 8 รายการ
   - ยอดรวม: ฿125,000

🟠 **คอมมิชชั่นรออนุมัติ:** 5 รายการ
   - ยอดรวม: ฿85,000

⚠️ แนะนำให้ดำเนินการรายการถอนก่อน`,
    suggestions: ['ดูรายการถอนรอดำเนินการ', 'อนุมัติทั้งหมด', 'ดูรายการที่รอนาน'],
  },
  'มีการแจ้งเตือนอะไรบ้าง': {
    content: `🔔 **การแจ้งเตือนที่สำคัญ**

🔴 **ด่วน:**
- บัญชีกสิกร ใกล้เต็มวงเงิน (92%)
- API JILI มีปัญหาเชื่อมต่อ

🟡 **แจ้งเตือน:**
- สมาชิก VIP ถอน ฿500,000 รอดำเนินการ
- โปรโมชั่น "ฝาก 100 รับ 200" หมดอายุใน 2 ชม.

🟢 **ข้อมูล:**
- ยอดฝากวันนี้สูงกว่าเป้า 15%
- สมาชิกใหม่เกินเป้ารายวัน`,
    suggestions: ['ดูการแจ้งเตือนทั้งหมด', 'ตั้งค่าการแจ้งเตือน', 'ปิดการแจ้งเตือน'],
  },
}

export function AIAssistant() {
  const [enabled, setEnabled] = useState(true)
  const [showQuickActions, setShowQuickActions] = useState(true)
  const [isOpen, setIsOpen] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const systemPromptLoadedRef = useRef(false) // ✅ ใช้ ref แทน state เพื่อหลีกเลี่ยง re-render
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content: 'สวัสดีครับ! 👋 ผมเป็น AI Assistant พร้อมช่วยเหลือคุณจัดการระบบ\n\nคุณสามารถถามเกี่ยวกับ:\n- ยอดฝาก-ถอน\n- สมาชิก\n- รายการรอดำเนินการ\n- และอื่นๆ',
      timestamp: new Date(),
      suggestions: ['สรุปยอดวันนี้', 'สมาชิกใหม่', 'รายการรอดำเนินการ'],
    },
  ])
  const [input, setInput] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Load AI Agent config (with auto-refresh)
  useEffect(() => {
    let isMounted = true
    let intervalId: NodeJS.Timeout | null = null
    
    const loadConfig = async () => {
      if (!isMounted) return
      
      try {
        const service = getAppConfigService()
        const config = await service.getAppConfig()
        
        if (!isMounted) return
        
        console.log('🤖 [AIAssistant] Loaded config:', config)
        console.log('🤖 [AIAssistant] AI Agent Config:', config?.aiAgentConfig)
        
        if (config?.aiAgentConfig) {
          const newEnabled = config.aiAgentConfig.enabled ?? true
          console.log('🤖 [AIAssistant] Enabled status:', newEnabled)
          
          if (!isMounted) return
          
          // ✅ ใช้ functional update เพื่อหลีกเลี่ยง state update ในระหว่าง render
          setEnabled((prevEnabled) => {
            if (!isMounted) return prevEnabled
            return newEnabled
          })
          
          // ✅ แยก logic การปิดหน้าต่างออกเป็น useEffect แยก (ดูด้านล่าง)
          
          if (!isMounted) return
          
          setShowQuickActions(config.aiAgentConfig.showQuickActions ?? true)
          
          if (config.aiAgentConfig.autoExpand && isMounted) {
            setIsExpanded(true)
          }
          
          // Update initial message with system prompt if available (แค่ครั้งแรก)
          if (config.aiAgentConfig.systemPrompt && !systemPromptLoadedRef.current && isMounted) {
            setMessages([{
              id: '1',
              role: 'assistant',
              content: config.aiAgentConfig.systemPrompt,
              timestamp: new Date(),
              suggestions: config.aiAgentConfig.showQuickActions ? ['สรุปยอดวันนี้', 'สมาชิกใหม่', 'รายการรอดำเนินการ'] : undefined,
            }])
            systemPromptLoadedRef.current = true
          }
        } else {
          // ถ้าไม่มี config ให้ใช้ default (enabled = true)
          if (!isMounted) return
          console.log('🤖 [AIAssistant] No config found - using default (enabled = true)')
          setEnabled(true)
        }
      } catch (error) {
        console.error('[AIAssistant] Load config error:', error)
        // ถ้ามี error ให้ใช้ default (enabled = true) เพื่อไม่ให้ app crash
        if (isMounted) {
          setEnabled(true)
        }
      }
    }
    
    // โหลดครั้งแรก
    loadConfig()
    
    // Refresh ทุก 5 วินาที (เพื่อให้อัปเดตเมื่อมีการเปลี่ยนแปลงใน Settings)
    intervalId = setInterval(() => {
      if (isMounted) {
        loadConfig()
      }
    }, 5000) // 5 seconds
    
    return () => {
      isMounted = false
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, []) // ✅ ใช้ empty dependency เพื่อให้ทำงานแค่ครั้งเดียวตอน mount

  // ✅ แยก useEffect สำหรับปิดหน้าต่างเมื่อ disabled (เพื่อหลีกเลี่ยง infinite loop)
  const wasEnabledRef = useRef(true) // Track previous enabled state
  useEffect(() => {
    // เช็คว่าเปลี่ยนจาก enabled → disabled
    if (wasEnabledRef.current && !enabled) {
      console.log('🤖 [AIAssistant] Disabled - closing window')
      setIsOpen(false)
    }
    wasEnabledRef.current = enabled
  }, [enabled]) // ✅ ใช้แค่ enabled เพื่อหลีกเลี่ยง infinite loop

  // ✅ ย้าย hooks ทั้งหมดไปไว้ก่อน early return เพื่อหลีกเลี่ยง React hooks error
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages])

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  // Don't render if disabled
  // ✅ ใช้ early return เพื่อไม่ให้ render component เลย (แต่ hooks ต้องอยู่ก่อน early return)
  if (!enabled) {
    return null
  }

  const handleSend = async (text?: string) => {
    const messageText = text || input.trim()
    if (!messageText) return

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: messageText,
      timestamp: new Date(),
    }
    setMessages((prev) => [...prev, userMessage])
    setInput('')
    setIsTyping(true)

    // Simulate AI response
    setTimeout(() => {
      const response = mockResponses[messageText] || {
        content: `ขอบคุณสำหรับคำถาม! ผมกำลังประมวลผล...\n\nขณะนี้ผมสามารถช่วยตอบคำถามเกี่ยวกับ:\n- สรุปยอดฝาก-ถอน\n- ข้อมูลสมาชิก\n- รายการรอดำเนินการ\n- การแจ้งเตือนระบบ\n\nลองถามคำถามเหล่านี้ดูครับ!`,
        suggestions: ['สรุปยอดวันนี้', 'สมาชิกใหม่', 'รายการรอดำเนินการ'],
      }

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.content,
        timestamp: new Date(),
        suggestions: response.suggestions,
      }
      setMessages((prev) => [...prev, assistantMessage])
      setIsTyping(false)
    }, 1000)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <>
      {/* Chat Button */}
      <Button
        onClick={() => setIsOpen(true)}
        className={cn(
          "fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg z-50 transition-all",
          "bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700",
          isOpen && "scale-0 opacity-0"
        )}
      >
        <Bot className="h-6 w-6" />
        <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-win animate-pulse" />
      </Button>

      {/* Chat Window */}
      <div
        className={cn(
          "fixed z-50 transition-all duration-300 ease-out",
          isExpanded
            ? "inset-4 md:inset-8"
            : "bottom-6 right-6 w-96 h-[600px] max-h-[80vh]",
          isOpen ? "scale-100 opacity-100" : "scale-95 opacity-0 pointer-events-none"
        )}
      >
        <div className="flex flex-col h-full bg-background border rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-purple-600 to-blue-600 text-white">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center">
                <Bot className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold">AI Assistant</h3>
                <div className="flex items-center gap-1.5 text-xs text-white/80">
                  <span className="h-2 w-2 rounded-full bg-win animate-pulse" />
                  พร้อมช่วยเหลือ
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-white hover:bg-white/20"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-white hover:bg-white/20"
                onClick={() => setIsOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Quick Actions */}
          {showQuickActions && (
            <div className="p-3 border-b bg-muted/30">
              <div className="flex gap-2 overflow-x-auto pb-1">
                {quickActions.map((action, idx) => (
                  <Button
                    key={idx}
                    variant="outline"
                    size="sm"
                    className="shrink-0 gap-1.5 text-xs h-8"
                    onClick={() => handleSend(action.query)}
                  >
                    {action.icon}
                    {action.label}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
          <ScrollArea className="flex-1 p-4" ref={scrollRef}>
            <div className="space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={cn(
                    "flex gap-3",
                    message.role === 'user' && "flex-row-reverse"
                  )}
                >
                  <div
                    className={cn(
                      "h-8 w-8 rounded-full flex items-center justify-center shrink-0",
                      message.role === 'assistant'
                        ? "bg-gradient-to-r from-purple-600 to-blue-600 text-white"
                        : "bg-primary text-primary-foreground"
                    )}
                  >
                    {message.role === 'assistant' ? (
                      <Bot className="h-4 w-4" />
                    ) : (
                      <User className="h-4 w-4" />
                    )}
                  </div>
                  <div
                    className={cn(
                      "max-w-[80%] space-y-2",
                      message.role === 'user' && "text-right"
                    )}
                  >
                    <div
                      className={cn(
                        "rounded-2xl px-4 py-2.5 text-sm",
                        message.role === 'assistant'
                          ? "bg-muted text-foreground rounded-tl-none"
                          : "bg-primary text-primary-foreground rounded-tr-none"
                      )}
                    >
                      <div className="whitespace-pre-wrap">{message.content}</div>
                    </div>
                    {message.suggestions && message.suggestions.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {message.suggestions.map((suggestion, idx) => (
                          <Button
                            key={idx}
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs rounded-full"
                            onClick={() => handleSend(suggestion)}
                          >
                            {suggestion}
                          </Button>
                        ))}
                      </div>
                    )}
                    <p className="text-[10px] text-muted-foreground">
                      {message.timestamp.toLocaleTimeString('th-TH', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="flex gap-3">
                  <div className="h-8 w-8 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 text-white flex items-center justify-center">
                    <Bot className="h-4 w-4" />
                  </div>
                  <div className="bg-muted rounded-2xl rounded-tl-none px-4 py-3">
                    <div className="flex gap-1">
                      <span className="h-2 w-2 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="h-2 w-2 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="h-2 w-2 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Input */}
          <div className="p-4 border-t bg-background">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="พิมพ์ข้อความ..."
                className="flex-1 rounded-full"
                disabled={isTyping}
              />
              <Button
                onClick={() => handleSend()}
                disabled={!input.trim() || isTyping}
                className="h-10 w-10 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
              >
                {isTyping ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
            <p className="text-[10px] text-center text-muted-foreground mt-2">
              AI Assistant • Powered by GPT
            </p>
          </div>
        </div>
      </div>

      {/* Backdrop */}
      {isOpen && isExpanded && (
        <div
          className="fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsExpanded(false)}
        />
      )}
    </>
  )
}










